/*
 * File: ClientRowGrid.tsx
 * Project: a-a
 * File Created: Saturday, 21st December 2024 9:53:17 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Friday, 5th December 2025 9:37:19 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2024 Mutual Mobile, Mutual Mobile
 */
import { Box, Chip, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Link, useNavigate } from 'react-router-dom';
import formatCurrency from '../../../helper/short-currency';
import zeplinColor from '../../../theme/Color';

type Client = {
  Name: string;
  AccountId: string;
  closedDealAmount: string | null;
  openDealAmount: string | null;
  category: string | null;
  source?: string | null;
};

interface Column {
  id:
    | 'Name'
    | 'AccountId'
    | 'category'
    | 'closedDealAmount'
    | 'openDealAmount'
    | 'totalFollowers'
    | 'source';
  label: string;
  minWidth?: number;
  align?: 'center';
  format?: (value: number) => string;
}

const columns: readonly Column[] = [
  { id: 'Name', label: 'Name' },
  { id: 'source', label: 'Added via' },
  { id: 'category', label: 'Category', minWidth: 100 },
  {
    id: 'closedDealAmount',
    label: 'Close deals',

    align: 'center',
  },
  {
    id: 'openDealAmount',
    label: 'Open deals',

    align: 'center',
  },
];
type ClientData = {
  clients: [
    {
      Name: string;
      AccountId: string;
      closedDealAmount: string | null;
      openDealAmount: string | null;
      category: string | null;
      avatar?: string | null;
      source?: string | null;
    },
  ];
};
const ClientRowGrid = ({
  clients,
  loading,
  category,
}: {
  clients: ClientData;
  loading: boolean;
  category: string | null;
}) => {
  const screenHeight = window.innerHeight;
  const navigate = useNavigate();
  const data =
    category === 'all'
      ? clients?.clients
      : clients?.clients.filter((val) => val.category === category);
  return (
    <Paper sx={{ width: '100%', border: 'none', boxShadow: 'none' }}>
      <TableContainer
        sx={{
          overflow: 'scroll',
          height: { xs: 'calc(100vh - 190px)', xl: 'calc(100vh - 250px)' },
          // height: window.innerHeight - 250,
          // screenHeight > 1200
          //   ? '74vh'
          //   : screenHeight > 1100
          //   ? '72vh'
          //   : screenHeight > 1000
          //   ? '69vh'
          //   : screenHeight > 900
          //   ? '58vh'
          //   : screenHeight > 800
          //   ? '46vh'
          //   : screenHeight > 700
          //   ? '38vh'
          //   : screenHeight > 600
          //   ? '25vh'
          //   : '15vh',
        }}
      >
        <Table stickyHeader aria-label='sticky table'>
          <TableHead>
            <TableRow
              sx={{
                // backgroundColor: '#f5f5f5', // Table header background color
                border: '1px solid black',
                borderRadius: '14px', // Apply border radius to the table header row
                '& th': {
                  backgroundColor: '#f5f5f5',
                },
                '& th:first-child': {
                  borderRadius: '14px 0px 0px 14px',
                },
                '& th:last-child': {
                  borderRadius: '0px 14px 14px 0px',
                },
              }}
            >
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, ind) => {
              return (
                <TableRow
                  hover
                  role='checkbox'
                  tabIndex={-1}
                  key={ind}
                  sx={{
                    '&:last-child td, &:last-child th': { borderBottom: '2px solid #ddd' }, // Apply bottom border for each row
                  }}
                >
                  {columns.map((column) => {
                    const value =
                      column.id === 'closedDealAmount' || column.id === 'openDealAmount'
                        ? `$${formatCurrency(+(row[column.id] || 0))}`
                        : row[column.id as keyof Client];
                    return (
                      <TableCell key={column.id} align={column.align}>
                        <Link
                          to={row?.source === 'Huddle' ? `huddle/${row.AccountId}` : row.AccountId}
                          state={{ client: row }}
                          style={{ textDecoration: 'none', color: zeplinColor.BasicBlack }}
                        >
                          {column.id === 'Name' ? (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                cursor: 'pointer',
                              }}
                              // onClick={() => navigate(row?.AccountId)}
                            >
                              {row?.avatar ? (
                                <img
                                  width={'60px'}
                                  height={'60px'}
                                  src={row?.avatar}
                                  alt='logo'
                                  style={{ objectFit: 'cover', borderRadius: '14px' }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: '60px',
                                    height: '60px',
                                    borderRadius: '14px',
                                    //   border: '1px solid black',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor:
                                      row?.category === 'Talent'
                                        ? zeplinColor.Brand1
                                        : row?.category === 'Athlete'
                                        ? zeplinColor.USER_PROFILE_COLOR1
                                        : row?.category === 'Business'
                                        ? zeplinColor.USER_PROFILE_COLOR2
                                        : row?.category === 'Business_Development'
                                        ? zeplinColor.USER_PROFILE_COLOR3
                                        : '',
                                  }}
                                >
                                  <Typography
                                    variant='display5'
                                    sx={{ color: zeplinColor.Neutral1 }}
                                  >
                                    {value
                                      ?.split(' ')
                                      .map((val, index) =>
                                        index === 0 || index === value?.split(' ').length - 1
                                          ? val[0]
                                          : '',
                                      )}
                                  </Typography>
                                </Box>
                              )}
                              {value ?? 0}
                            </Box>
                          ) : column.id === 'category' || column.id === 'source' ? (
                            <Box>
                              <Chip
                                label={
                                  <Typography
                                    variant='label1'
                                    color={
                                      value === 'Huddle'
                                        ? zeplinColor.BasicBlack
                                        : zeplinColor.Neutral1
                                    }
                                    sx={{
                                      textAlign: 'center',
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                    }}
                                  >
                                    {value === 'Business_Development'
                                      ? value
                                          .split('_')
                                          .map((word) => word[0].toUpperCase())
                                          .join('')
                                      : value || 'NA'}
                                  </Typography>
                                }
                                sx={{
                                  backgroundColor:
                                    column.id === 'category'
                                      ? zeplinColor.BasicBlack
                                      : column.id === 'source' && value === 'Huddle'
                                      ? zeplinColor.Brand2
                                      : zeplinColor.Blue,
                                  padding: '5px 12px 5px 12px',
                                  borderRadius: '50px',
                                  // width: '47px',
                                  height: '24px',
                                }}
                              />
                            </Box>
                          ) : column.format && typeof value === 'number' ? (
                            column.format(value)
                          ) : (
                            value
                          )}
                        </Link>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ClientRowGrid;

// : column.id === 'totalFollowers' ? (
//                             <Box sx={{ display: 'flex', justifyContent: 'center' }}>
//                               {value}{' '}
//                               <LightTooltip
//                                 title={
//                                   <Box
//                                     sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
//                                   >
//                                     <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
//                                       <Box sx={{ display: 'flex', gap: '9px' }}>
//                                         <img
//                                           width={'18px'}
//                                           height={'18px'}
//                                           src={'/assets/instagram.svg'}
//                                           alt='logo'
//                                           style={{ objectFit: 'fill' }}
//                                         />
//                                         <Typography variant='body3' color={zeplinColor.Neutral1}>
//                                           Instagram
//                                         </Typography>
//                                       </Box>
//                                       <Typography variant='body3' color={zeplinColor.Neutral1}>
//                                         2.9M
//                                       </Typography>
//                                     </Box>
//                                     <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
//                                       <Box sx={{ display: 'flex', gap: '9px' }}>
//                                         <img
//                                           width={'18px'}
//                                           height={'18px'}
//                                           src={'/assets/tiktok.svg'}
//                                           alt='logo'
//                                           style={{ objectFit: 'fill' }}
//                                         />
//                                         <Typography variant='body3' color={zeplinColor.Neutral1}>
//                                           TikTok
//                                         </Typography>
//                                       </Box>
//                                       <Typography variant='body3' color={zeplinColor.Neutral1}>
//                                         2.9M
//                                       </Typography>
//                                     </Box>

//                                     <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
//                                       <Box sx={{ display: 'flex', gap: '9px' }}>
//                                         <img
//                                           width={'18px'}
//                                           height={'18px'}
//                                           src={'/assets/youtube.svg'}
//                                           alt='logo'
//                                           style={{ objectFit: 'fill' }}
//                                         />
//                                         <Typography variant='body3' color={zeplinColor.Neutral1}>
//                                           Youtube
//                                         </Typography>
//                                       </Box>
//                                       <Typography variant='body3' color={zeplinColor.Neutral1}>
//                                         2.9M
//                                       </Typography>
//                                     </Box>
//                                     <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
//                                       <Box sx={{ display: 'flex', gap: '9px' }}>
//                                         <img
//                                           width={'18px'}
//                                           height={'18px'}
//                                           src={'/assets/x.svg'}
//                                           alt='logo'
//                                           style={{ objectFit: 'fill' }}
//                                         />
//                                         <Typography variant='body3' color={zeplinColor.Neutral1}>
//                                           Twitter
//                                         </Typography>
//                                       </Box>
//                                       <Typography variant='body3' color={zeplinColor.Neutral1}>
//                                         2.9M
//                                       </Typography>
//                                     </Box>
//                                     <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
//                                       <Box sx={{ display: 'flex', gap: '9px' }}>
//                                         <img
//                                           width={'18px'}
//                                           height={'18px'}
//                                           src={'/assets/facebook.svg'}
//                                           alt='logo'
//                                           style={{ objectFit: 'fill' }}
//                                         />
//                                         <Typography variant='body3' color={zeplinColor.Neutral1}>
//                                           Facebook
//                                         </Typography>
//                                       </Box>
//                                       <Typography variant='body3' color={zeplinColor.Neutral1}>
//                                         2.9M
//                                       </Typography>
//                                     </Box>
//                                     <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
//                                       <Box sx={{ display: 'flex', gap: '9px' }}>
//                                         <img
//                                           width={'18px'}
//                                           height={'18px'}
//                                           src={'/assets/snapchat.svg'}
//                                           alt='logo'
//                                           style={{ objectFit: 'fill' }}
//                                         />
//                                         <Typography variant='body3' color={zeplinColor.Neutral1}>
//                                           Snapchat
//                                         </Typography>
//                                       </Box>
//                                       <Typography variant='body3' color={zeplinColor.Neutral1}>
//                                         2.9M
//                                       </Typography>
//                                     </Box>
//                                   </Box>
//                                 }
//                                 placement='bottom-end'
//                                 style={{
//                                   backgroundColor: zeplinColor.BasicBlack,
//                                   borderRadius: '14px',
//                                   padding: '16px',
//                                   width: '184px',
//                                   // zIndex: 9999,
//                                 }}
//                               >
//                                 <img
//                                   width={'18px'}
//                                   height={'18px'}
//                                   src={'/assets/information-circle.svg'}
//                                   alt='logo'
//                                   style={{ objectFit: 'fill' }}
//                                 />
//                               </LightTooltip>
//                             </Box>
//                           )
